import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/AuthContext";
import { PropertyProvider } from "@/lib/PropertyContext";
import { BrandProvider } from "@/lib/BrandContext";
import { I18nProvider } from "@/lib/i18n";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import BackgroundClouds from "@/components/layout/BackgroundClouds";
import CookieConsent from "@/components/CookieConsent";
import ExchangeDeclaration from "@/components/ExchangeDeclaration";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import AskVenturazWidget from "@/components/chat/AskVenturazWidget";

// Public
import Home from "@/pages/Home";
import ForStartups from "@/pages/ForStartups";
import ForInvestors from "@/pages/ForInvestors";
import HowItWorks from "@/pages/HowItWorks";
import Pricing from "@/pages/Pricing";
import Resources from "@/pages/Resources";
import BlogDetail from "@/pages/BlogDetail";
import SectorReports from "@/pages/SectorReports";
import Support from "@/pages/Support";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthChooser from "@/pages/AuthChooser";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import StartupApply from "@/pages/StartupApply";
import InvestorJoin from "@/pages/InvestorJoin";
import AdminLogin from "@/pages/AdminLogin";
import Marketplace from "@/pages/Marketplace";
import EsignSigner from "@/pages/EsignSigner";
import EsignDashboard from "@/pages/EsignDashboard";
import StartupDetail from "@/pages/StartupDetail";
import CMSPage from "@/pages/CMSPage";
import DealRoomDetail from "@/pages/DealRoomDetail";
import Messages from "@/pages/Messages";
import Wallet from "@/pages/Wallet";

// Startup
import StartupDashboard from "@/pages/startup/Dashboard";
import StartupProfile from "@/pages/startup/Profile";
import StartupKYC from "@/pages/startup/KYC";
import StartupVerification from "@/pages/startup/Verification";
import DigilockerCallback from "@/pages/kyc/DigilockerCallback";
import StartupFundingProfile from "@/pages/startup/FundingProfile";
import StartupDocuments from "@/pages/startup/Documents";
import StartupMatches from "@/pages/startup/Matches";
import StartupInvestorInterest from "@/pages/startup/InvestorInterest";
import StartupQueries from "@/pages/startup/Queries";
import StartupDealRooms from "@/pages/startup/DealRooms";
import StartupSettings from "@/pages/startup/Settings";
import StartupCompanyIntelligence from "@/pages/startup/CompanyIntelligence";
import StartupFinancialProfile from "@/pages/startup/FinancialProfile";
import StartupInvestorDigestPreview from "@/pages/startup/InvestorDigestPreview";

// Investor
import InvestorDashboard from "@/pages/investor/Dashboard";
import InvestorProfile from "@/pages/investor/Profile";
import InvestorPreferences from "@/pages/investor/Preferences";
import InvestorKYC from "@/pages/investor/KYC";
import InvestorVerification from "@/pages/investor/Verification";
import InvestorMarketplace from "@/pages/investor/Marketplace";
import InvestorSaved from "@/pages/investor/SavedStartups";
import InvestorWatchlist from "@/pages/investor/Watchlist";
import InvestorTrackRecord from "@/pages/investor/TrackRecord";
import InvestorInterests from "@/pages/investor/Interests";
import InvestorQueries from "@/pages/investor/Queries";
import InvestorDealRooms from "@/pages/investor/DealRooms";
import InvestorDiligenceOrders from "@/pages/investor/DiligenceOrders";
import InvestorSettings from "@/pages/investor/Settings";

// Admin
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminStartups from "@/pages/admin/Startups";
import AdminKycQueue from "@/pages/admin/KycQueue";
import AdminInvestors from "@/pages/admin/Investors";
import AdminInterests from "@/pages/admin/Interests";
import AdminQueries from "@/pages/admin/Queries";
import AdminDealRooms from "@/pages/admin/DealRooms";
import AdminUsers from "@/pages/admin/Users";
import AdminPlans from "@/pages/admin/Plans";
import AdminEsignPricing from "@/pages/admin/EsignPricing";
import AdminReports from "@/pages/admin/Reports";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminMatchOverrides from "@/pages/admin/MatchOverrides";
import AdminTasks from "@/pages/admin/Tasks";
import AdminNotifications from "@/pages/admin/Notifications";
import AdminCMS from "@/pages/admin/CMS";
import AdminBlogs from "@/pages/admin/Blogs";
import AdminSettings from "@/pages/admin/Settings";
import AdminHeroPulse from "@/pages/admin/HeroPulse";
import AdminUserPropertyAccess from "@/pages/admin/UserPropertyAccess";
import { AdminCompanyIntelligenceList, AdminCompanyIntelligenceDetail, AdminMCALookup } from "@/pages/admin/CompanyIntelligence";
import { AdminFinancialReviewList, AdminFinancialReviewDetail } from "@/pages/admin/FinancialReview";
import { AdminDDReportsList, AdminDDReportDetail } from "@/pages/admin/DDReports";
import AdminDeepDD from "@/pages/admin/DeepDD";
import AdminCoupons from "@/pages/admin/Coupons";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminBillingSeller from "@/pages/admin/BillingSeller";
import AdminEmailSchedule from "@/pages/admin/EmailSchedule";
import UserInvoices from "@/pages/UserInvoices";
import NotificationPreferences from "@/pages/NotificationPreferences";
import { InvestorDDReportsList, InvestorDDReportDetail } from "@/pages/investor/DDReports";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminNurtureMetrics from "@/pages/admin/NurtureMetrics";
import AdminMandateGraph from "@/pages/admin/MandateGraph";
import AdminDaasOrders from "@/pages/admin/DaasOrders";
import AdminDaasPricing from "@/pages/admin/DaasPricing";
import AdminMonitoring from "@/pages/admin/Monitoring";
import AdminWaitlistAnalytics from "@/pages/admin/WaitlistAnalytics";
import StartupDiligenceServices from "@/pages/startup/DiligenceServices";
import StartupFounderPremium from "@/pages/startup/FounderPremium";
import StartupPremiumAnalytics from "@/pages/startup/PremiumAnalytics";
import InvestorAlertPreferences from "@/pages/investor/AlertPreferences";
import AdminMarketReport from "@/pages/admin/MarketReport";
import AdminEmailLog from "@/pages/admin/EmailLog";
import AdminApprovalsCockpit from "@/pages/admin/ApprovalsCockpit";
import AdminOnboardingRequests from "@/pages/admin/OnboardingRequests";
import AdminProfileRequests from "@/pages/admin/ProfileRequests";
import KycSnapshots from "@/pages/admin/KycSnapshots";
import HeroSplitTest from "@/pages/admin/HeroSplitTest";
import AdminDocChangeRequests from "@/pages/admin/DocChangeRequests";
import DownloadForensics from "@/pages/admin/DownloadForensics";
import RaiseDebt from "@/pages/RaiseDebtLanding";
import DebtApplyWizard from "@/pages/DebtApplyWizard";
import DebtProductsPage from "@/pages/DebtProductsPage";
import DebtProductDetail from "@/pages/DebtProductDetail";
import DebtBanksPage from "@/pages/DebtBanksPage";
import DebtBecomePartner from "@/pages/DebtBecomePartner";
import RaiseDebtOnboard from "@/pages/RaiseDebtOnboard";
import MyDebtCases from "@/pages/MyDebtCases";
import AdminDebtCases from "@/pages/admin/DebtCases";
import AdminMoneyDashboard from "@/pages/admin/MoneyDashboard";
import AdminDebtClients from "@/pages/admin/DebtClients";
import AdminLenders from "@/pages/admin/Lenders";
import AdminSessions from "@/pages/admin/Sessions";
import AdminChannelPartners from "@/pages/admin/ChannelPartners";
import AdminDebtConfig from "@/pages/admin/DebtConfig";
import AdminRolesCMS from "@/pages/admin/RolesCMS";
import AdminEmployeeTargets from "@/pages/admin/EmployeeTargets";
import AdminIncentiveRules from "@/pages/admin/IncentiveRules";
import AdminDebtNotifications from "@/pages/admin/DebtNotifications";
import AdminDebtLeads from "@/pages/admin/DebtLeads";
import AdminIncentivePayouts from "@/pages/admin/IncentivePayouts";
import MyIncentives from "@/pages/MyIncentives";
import PartnerDashboard from "@/pages/partner/Dashboard";
import DealVelocityBoard from "@/pages/DealVelocityBoard";
import AccountProfile from "@/pages/AccountProfile";
import NotificationSettings from "@/pages/settings/NotificationSettings";
import RoleChangeRequest from "@/pages/settings/RoleChangeRequest";
import CheckoutReturn from "@/pages/CheckoutReturn";
import GoogleAuthReturn from "@/pages/GoogleAuthReturn";
import NotFound from "@/pages/NotFound";
import StartupPassport from "@/pages/passport/StartupPassport";
import PublicPassport from "@/pages/passport/PublicPassport";

const admin = ["super_admin", "admin", "manager", "agent"];
// Debt CRM access set — admins + the 4 pure-debt personas.
const debtTeam = ["super_admin", "admin", "manager", "agent", "debt_head", "debt_manager", "debt_agent", "credit_analyst"];

function AppRoutes() {
  const location = useLocation();
  // Handle Emergent OAuth session callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/for-startups" element={<ForStartups />} />
      <Route path="/for-investors" element={<ForInvestors />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/passport/share/:token" element={<PublicPassport />} />
      <Route path="/esign/sign/:envelopeId" element={<EsignSigner />} />
      <Route path="/esign" element={<ProtectedRoute><EsignDashboard /></ProtectedRoute>} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<BlogDetail />} />
      <Route path="/sector-reports" element={<SectorReports />} />
      <Route path="/support" element={<Support />} />
      <Route path="/login" element={<AuthChooser mode="login" />} />
      <Route path="/for-startups/login" element={<Login intent="equity" />} />
      <Route path="/money/login" element={<Login intent="debt" />} />
      <Route path="/for-investors/login" element={<Login intent="investor" />} />
      <Route path="/signup" element={<AuthChooser mode="signup" />} />
      <Route path="/for-startups/signup" element={<Signup intent="equity" />} />
      <Route path="/money/signup" element={<Signup intent="debt" />} />
      <Route path="/for-investors/signup" element={<Signup intent="investor" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/kyc/digilocker/callback" element={<DigilockerCallback />} />
      <Route path="/startup/apply" element={<StartupApply />} />
      <Route path="/investor/join" element={<InvestorJoin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/startup/:id" element={<StartupDetail />} />
      <Route path="/cms/:slug" element={<CMSPage />} />
      <Route path="/deal-room/:id" element={<ProtectedRoute><DealRoomDetail /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
      <Route path="/settings/profile" element={<ProtectedRoute><AccountProfile /></ProtectedRoute>} />
      <Route path="/settings/kyc" element={<ProtectedRoute><AccountProfile /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
      <Route path="/settings/role-change-request" element={<ProtectedRoute><RoleChangeRequest /></ProtectedRoute>} />
      <Route path="/checkout/return" element={<CheckoutReturn />} />
      <Route path="/auth/google/return" element={<GoogleAuthReturn />} />

      {/* Debt Marketplace — public form + user portal */}
      <Route path="/money" element={<RaiseDebt />} />
      <Route path="/money/apply/:product" element={<DebtApplyWizard />} />
      {/* Legacy path — kept as 301-style redirect for anyone with an old bookmark. */}
      <Route path="/raise-debt" element={<Navigate to="/money" replace />} />
      <Route path="/raise-debt/apply/:product" element={<Navigate to="/money" replace />} />
      <Route path="/venturaz-money" element={<Navigate to="/money" replace />} />
      <Route path="/venturaz-money/products" element={<DebtProductsPage />} />
      <Route path="/venturaz-money/product/:slug" element={<DebtProductDetail />} />
      <Route path="/venturaz-money/banks" element={<DebtBanksPage />} />
      <Route path="/venturaz-money/become-partner" element={<DebtBecomePartner />} />
      <Route path="/money/onboard" element={<ProtectedRoute><RaiseDebtOnboard /></ProtectedRoute>} />
      <Route path="/my-debt-cases" element={<ProtectedRoute><MyDebtCases /></ProtectedRoute>} />
      <Route path="/my-debt-cases/:id" element={<ProtectedRoute><MyDebtCases /></ProtectedRoute>} />

      {/* Startup */}
      <Route path="/startup/dashboard" element={<ProtectedRoute roles={["startup"]}><StartupDashboard /></ProtectedRoute>} />
      <Route path="/startup/profile" element={<ProtectedRoute roles={["startup"]}><StartupProfile /></ProtectedRoute>} />
      <Route path="/startup/verification" element={<ProtectedRoute roles={["startup"]}><StartupVerification /></ProtectedRoute>} />
      <Route path="/startup/kyc" element={<Navigate to="/startup/verification" replace />} />
      <Route path="/startup/kyc-legacy" element={<ProtectedRoute roles={["startup"]}><StartupKYC /></ProtectedRoute>} />
      <Route path="/startup/funding-profile" element={<ProtectedRoute roles={["startup"]}><StartupFundingProfile /></ProtectedRoute>} />
      <Route path="/startup/documents" element={<ProtectedRoute roles={["startup"]}><StartupDocuments /></ProtectedRoute>} />
      <Route path="/startup/matches" element={<ProtectedRoute roles={["startup"]}><StartupMatches /></ProtectedRoute>} />
      <Route path="/startup/investor-interest" element={<ProtectedRoute roles={["startup"]}><StartupInvestorInterest /></ProtectedRoute>} />
      <Route path="/startup/queries" element={<ProtectedRoute roles={["startup"]}><StartupQueries /></ProtectedRoute>} />
      <Route path="/startup/deal-rooms" element={<ProtectedRoute roles={["startup"]}><StartupDealRooms /></ProtectedRoute>} />
      <Route path="/startup/settings" element={<ProtectedRoute roles={["startup"]}><StartupSettings /></ProtectedRoute>} />
      <Route path="/startup/company-intelligence" element={<Navigate to="/startup/profile?tab=company" replace />} />
      <Route path="/startup/financial-profile" element={<ProtectedRoute roles={["startup"]}><StartupFinancialProfile /></ProtectedRoute>} />
      <Route path="/startup/investor-digest-preview" element={<ProtectedRoute roles={["startup"]}><StartupInvestorDigestPreview /></ProtectedRoute>} />

      {/* Investor */}
      <Route path="/investor/dashboard" element={<ProtectedRoute roles={["investor"]}><InvestorDashboard /></ProtectedRoute>} />
      <Route path="/investor/profile" element={<ProtectedRoute roles={["investor"]}><InvestorProfile /></ProtectedRoute>} />
      <Route path="/investor/preferences" element={<ProtectedRoute roles={["investor"]}><InvestorPreferences /></ProtectedRoute>} />
      <Route path="/investor/verification" element={<ProtectedRoute roles={["investor"]}><InvestorVerification /></ProtectedRoute>} />
      <Route path="/investor/kyc" element={<Navigate to="/investor/verification" replace />} />
      <Route path="/investor/kyc-legacy" element={<ProtectedRoute roles={["investor"]}><InvestorKYC /></ProtectedRoute>} />
      <Route path="/investor/marketplace" element={<ProtectedRoute roles={["investor"]}><InvestorMarketplace /></ProtectedRoute>} />
      <Route path="/investor/saved-startups" element={<ProtectedRoute roles={["investor"]}><InvestorSaved /></ProtectedRoute>} />
      <Route path="/investor/watchlist" element={<ProtectedRoute roles={["investor"]}><InvestorWatchlist /></ProtectedRoute>} />
      <Route path="/investor/track-record" element={<ProtectedRoute roles={["investor"]}><InvestorTrackRecord /></ProtectedRoute>} />
      <Route path="/investor/interests" element={<ProtectedRoute roles={["investor"]}><InvestorInterests /></ProtectedRoute>} />
      <Route path="/investor/queries" element={<ProtectedRoute roles={["investor"]}><InvestorQueries /></ProtectedRoute>} />
      <Route path="/investor/deal-rooms" element={<ProtectedRoute roles={["investor"]}><InvestorDealRooms /></ProtectedRoute>} />
      <Route path="/investor/diligence-orders" element={<ProtectedRoute roles={["investor"]}><InvestorDiligenceOrders /></ProtectedRoute>} />
      <Route path="/investor/settings" element={<ProtectedRoute roles={["investor"]}><InvestorSettings /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin/dashboard" element={<ProtectedRoute roles={admin}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/approvals" element={<ProtectedRoute roles={admin}><AdminApprovalsCockpit /></ProtectedRoute>} />
      <Route path="/admin/onboarding-requests" element={<ProtectedRoute roles={admin}><AdminOnboardingRequests /></ProtectedRoute>} />
      <Route path="/admin/startups" element={<ProtectedRoute roles={admin}><AdminStartups /></ProtectedRoute>} />
      <Route path="/admin/investors" element={<ProtectedRoute roles={admin}><AdminInvestors /></ProtectedRoute>} />
      <Route path="/admin/interests" element={<ProtectedRoute roles={admin}><AdminInterests /></ProtectedRoute>} />
      <Route path="/admin/queries" element={<ProtectedRoute roles={admin}><AdminQueries /></ProtectedRoute>} />
      <Route path="/admin/deal-rooms" element={<ProtectedRoute roles={admin}><AdminDealRooms /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={admin}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/profile-requests" element={<ProtectedRoute roles={admin}><AdminProfileRequests /></ProtectedRoute>} />
      <Route path="/admin/managers" element={<ProtectedRoute roles={admin}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/agents" element={<ProtectedRoute roles={admin}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/plans" element={<ProtectedRoute roles={admin}><AdminPlans /></ProtectedRoute>} />
      <Route path="/admin/esign-pricing" element={<ProtectedRoute roles={admin}><AdminEsignPricing /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute roles={admin}><AdminPlans /></ProtectedRoute>} />
      <Route path="/admin/matches" element={<ProtectedRoute roles={admin}><AdminInterests /></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute roles={admin}><AdminStartups /></ProtectedRoute>} />
      <Route path="/admin/kyc" element={<ProtectedRoute roles={admin}><AdminKycQueue /></ProtectedRoute>} />
      <Route path="/admin/kyc-queue" element={<ProtectedRoute roles={admin}><AdminKycQueue /></ProtectedRoute>} />
      <Route path="/admin/kyc-snapshots/:entityType/:entityId" element={<ProtectedRoute roles={admin}><KycSnapshots /></ProtectedRoute>} />
      <Route path="/admin/hero-split-test" element={<ProtectedRoute roles={admin}><HeroSplitTest /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute roles={admin}><AdminReports /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute roles={admin}><AdminAuditLogs /></ProtectedRoute>} />
      <Route path="/admin/match-overrides" element={<ProtectedRoute roles={admin}><AdminMatchOverrides /></ProtectedRoute>} />
      <Route path="/admin/doc-change-requests" element={<ProtectedRoute roles={admin}><AdminDocChangeRequests /></ProtectedRoute>} />
      <Route path="/admin/download-forensics" element={<ProtectedRoute roles={admin}><DownloadForensics /></ProtectedRoute>} />

      {/* Debt Marketplace — admin CRM */}
      <Route path="/admin/debt" element={<ProtectedRoute roles={debtTeam}><AdminDebtCases /></ProtectedRoute>} />
      <Route path="/admin/money/dashboard" element={<ProtectedRoute roles={debtTeam}><AdminMoneyDashboard /></ProtectedRoute>} />
      <Route path="/admin/debt/clients" element={<ProtectedRoute roles={debtTeam}><AdminDebtClients /></ProtectedRoute>} />
      <Route path="/admin/debt/clients/:id" element={<ProtectedRoute roles={debtTeam}><AdminDebtClients /></ProtectedRoute>} />
      <Route path="/admin/debt/:id" element={<ProtectedRoute roles={debtTeam}><AdminDebtCases /></ProtectedRoute>} />
      <Route path="/admin/lenders" element={<ProtectedRoute roles={debtTeam}><AdminLenders /></ProtectedRoute>} />
      <Route path="/admin/sessions" element={<ProtectedRoute roles={["super_admin","admin","manager"]}><AdminSessions /></ProtectedRoute>} />
      <Route path="/admin/channel-partners" element={<ProtectedRoute roles={debtTeam}><AdminChannelPartners /></ProtectedRoute>} />
      <Route path="/admin/debt/config" element={<ProtectedRoute roles={debtTeam}><AdminDebtConfig /></ProtectedRoute>} />

      {/* Debt CRM · Phase 4 — Roles CMS · Targets · Incentives · Notifications */}
      <Route path="/admin/roles-cms" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminRolesCMS /></ProtectedRoute>} />
      <Route path="/admin/targets" element={<ProtectedRoute roles={admin}><AdminEmployeeTargets /></ProtectedRoute>} />
      <Route path="/admin/incentive-rules" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminIncentiveRules /></ProtectedRoute>} />
      <Route path="/admin/debt/notifications" element={<ProtectedRoute roles={debtTeam}><AdminDebtNotifications /></ProtectedRoute>} />
      <Route path="/admin/debt/leads" element={<ProtectedRoute roles={debtTeam}><AdminDebtLeads /></ProtectedRoute>} />
      <Route path="/admin/incentive-payouts" element={<ProtectedRoute roles={admin}><AdminIncentivePayouts /></ProtectedRoute>} />
      <Route path="/my-incentives" element={<ProtectedRoute><MyIncentives /></ProtectedRoute>} />

      {/* Channel Partner portal */}
      <Route path="/partner/dashboard" element={<ProtectedRoute roles={["channel_partner"]}><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/deal-velocity" element={<ProtectedRoute><DealVelocityBoard /></ProtectedRoute>} />
      <Route path="/admin/tasks" element={<ProtectedRoute roles={admin}><AdminTasks /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotifications /></ProtectedRoute>} />
      <Route path="/admin/cms" element={<ProtectedRoute roles={admin}><AdminCMS /></ProtectedRoute>} />
      <Route path="/admin/blogs" element={<ProtectedRoute roles={admin}><AdminBlogs /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={admin}><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/hero-pulse" element={<ProtectedRoute roles={["super_admin"]}><AdminHeroPulse /></ProtectedRoute>} />
      <Route path="/admin/user-property-access" element={<ProtectedRoute roles={["super_admin"]}><AdminUserPropertyAccess /></ProtectedRoute>} />
      <Route path="/admin/company-intelligence" element={<ProtectedRoute roles={admin}><AdminCompanyIntelligenceList /></ProtectedRoute>} />
      <Route path="/admin/company-intelligence/:startupId" element={<ProtectedRoute roles={admin}><AdminCompanyIntelligenceDetail /></ProtectedRoute>} />
      <Route path="/admin/mca-lookup" element={<ProtectedRoute roles={admin}><AdminMCALookup /></ProtectedRoute>} />
      <Route path="/admin/financial-review" element={<ProtectedRoute roles={admin}><AdminFinancialReviewList /></ProtectedRoute>} />
      <Route path="/admin/financial-review/:startupId" element={<ProtectedRoute roles={admin}><AdminFinancialReviewDetail /></ProtectedRoute>} />
      <Route path="/admin/dd-reports" element={<ProtectedRoute roles={admin}><AdminDDReportsList /></ProtectedRoute>} />
      <Route path="/admin/deep-dd" element={<ProtectedRoute roles={admin}><AdminDeepDD /></ProtectedRoute>} />
      <Route path="/admin/coupons" element={<ProtectedRoute roles={admin}><AdminCoupons /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute roles={admin}><AdminInvoices /></ProtectedRoute>} />
      <Route path="/admin/billing-seller" element={<ProtectedRoute roles={admin}><AdminBillingSeller /></ProtectedRoute>} />
      <Route path="/admin/email-schedule" element={<ProtectedRoute roles={admin}><AdminEmailSchedule /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><UserInvoices /></ProtectedRoute>} />
      <Route path="/notifications/preferences" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
      <Route path="/admin/dd-reports/:startupId/:reportId" element={<ProtectedRoute roles={admin}><AdminDDReportDetail /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={admin}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/nurture-metrics" element={<ProtectedRoute roles={admin}><AdminNurtureMetrics /></ProtectedRoute>} />
      <Route path="/admin/mandate-graph" element={<ProtectedRoute roles={admin}><AdminMandateGraph /></ProtectedRoute>} />
      <Route path="/admin/daas-orders" element={<ProtectedRoute roles={admin}><AdminDaasOrders /></ProtectedRoute>} />
      <Route path="/admin/daas-pricing" element={<ProtectedRoute roles={admin}><AdminDaasPricing /></ProtectedRoute>} />
      <Route path="/admin/monitoring" element={<ProtectedRoute roles={admin}><AdminMonitoring /></ProtectedRoute>} />
      <Route path="/admin/waitlist-analytics" element={<ProtectedRoute roles={admin}><AdminWaitlistAnalytics /></ProtectedRoute>} />
      <Route path="/startup/diligence-services" element={<ProtectedRoute roles={["startup"]}><StartupDiligenceServices /></ProtectedRoute>} />
      <Route path="/startup/founder-premium" element={<ProtectedRoute roles={["startup"]}><StartupFounderPremium /></ProtectedRoute>} />
      <Route path="/startup/premium-analytics" element={<ProtectedRoute roles={["startup"]}><StartupPremiumAnalytics /></ProtectedRoute>} />
      <Route path="/investor/alert-preferences" element={<ProtectedRoute roles={["investor"]}><InvestorAlertPreferences /></ProtectedRoute>} />
      <Route path="/admin/market-report" element={<ProtectedRoute roles={admin}><AdminMarketReport /></ProtectedRoute>} />
      <Route path="/admin/email-log" element={<ProtectedRoute roles={admin}><AdminEmailLog /></ProtectedRoute>} />
      <Route path="/startup/passport" element={<ProtectedRoute roles={["startup"]}><StartupPassport scope="owner" /></ProtectedRoute>} />
      <Route path="/admin/startup-passport/:startupId" element={<ProtectedRoute roles={admin}><StartupPassport scope="admin" /></ProtectedRoute>} />
      <Route path="/investor/startup-passport/:startupId" element={<ProtectedRoute roles={["investor"]}><StartupPassport scope="investor" /></ProtectedRoute>} />
      <Route path="/investor/dd-reports/:startupId" element={<ProtectedRoute roles={["investor"]}><InvestorDDReportsList /></ProtectedRoute>} />
      <Route path="/investor/dd-reports/:startupId/:reportId" element={<ProtectedRoute roles={["investor"]}><InvestorDDReportDetail /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen text-ink relative">
      <BackgroundClouds />
      <ErrorBoundary>
        <BrowserRouter>
          <I18nProvider>
            <AuthProvider>
              <PropertyProvider>
                <BrandProvider>
                  <MaintenanceBanner />
                  <AppRoutes />
                  <CookieConsent />
                  <ExchangeDeclaration />
                  <AskVenturazWidget />
                </BrandProvider>
              </PropertyProvider>
            </AuthProvider>
          </I18nProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
